import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Truck, MapPin, ChevronRight, Lock, Check, ShoppingBag, ArrowLeft } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import './Checkout.css';

const Checkout = () => {
  const { siteName, orderPrefix } = useSite();
  const { cartItems, getTotalPrice, getTotalItems, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });

  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
  });

  const [billingSame, setBillingSame] = useState(true);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0 && !orderComplete) {
      navigate('/shop');
    }
  }, [cartItems, orderComplete, navigate]);

  // Pre-fill user info if logged in
  useEffect(() => {
    if (user) {
      setShippingInfo((prev) => ({
        ...prev,
        email: user.email || prev.email,
        firstName: user.name?.split(' ')[0] || prev.firstName,
        lastName: user.name?.split(' ').slice(1).join(' ') || prev.lastName,
      }));
    }
  }, [user]);

  const subtotal = getTotalPrice();
  const shipping = subtotal >= 49 ? 0 : 8.95;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const steps = [
    { id: 1, name: 'Shipping', icon: MapPin },
    { id: 2, name: 'Payment', icon: CreditCard },
    { id: 3, name: 'Review', icon: Check },
  ];

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Format card number
    if (name === 'cardNumber') {
      formattedValue = value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim();
      if (formattedValue.length > 19) formattedValue = formattedValue.slice(0, 19);
    }

    // Format expiry
    if (name === 'expiry') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length >= 2) {
        formattedValue = formattedValue.slice(0, 2) + '/' + formattedValue.slice(2, 4);
      }
    }

    // Format CVV
    if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setPaymentInfo((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const validateShipping = () => {
    const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zipCode'];
    return required.every((field) => shippingInfo[field].trim() !== '');
  };

  const validatePayment = () => {
    return (
      paymentInfo.cardNumber.replace(/\s/g, '').length >= 15 &&
      paymentInfo.cardName.trim() !== '' &&
      paymentInfo.expiry.length === 5 &&
      paymentInfo.cvv.length >= 3
    );
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !validateShipping()) {
      alert('Please fill in all shipping information');
      return;
    }
    if (currentStep === 2 && !validatePayment()) {
      alert('Please fill in all payment information');
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const handlePlaceOrder = async () => {
    setIsProcessing(true);

    // Simulate order processing
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setOrderComplete(true);
    clearCart();
    setIsProcessing(false);
  };

  if (orderComplete) {
    return (
      <div className="checkout-page">
        <div className="checkout-complete">
          <div className="checkout-complete-icon">
            <Check size={48} />
          </div>
          <h1 className="checkout-complete-title">Order Confirmed!</h1>
          <p className="checkout-complete-message">
            Thank you for shopping at {siteName}. Your order has been placed successfully.
          </p>
          <p className="checkout-complete-order">
            Order Number: <strong>#{orderPrefix}{Date.now().toString().slice(-8)}</strong>
          </p>
          <p className="checkout-complete-email">
            A confirmation email has been sent to <strong>{shippingInfo.email}</strong>
          </p>
          <div className="checkout-complete-actions">
            <Link to="/shop" className="checkout-complete-btn">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      {/* Breadcrumb */}
      <nav className="checkout-breadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight size={14} />
        <Link to="/shop">Shop</Link>
        <ChevronRight size={14} />
        <span>Checkout</span>
      </nav>

      {/* Steps */}
      <div className="checkout-steps">
        {steps.map((step, index) => (
          <div key={step.id} className={`checkout-step ${currentStep >= step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}>
            <div className="checkout-step-icon">
              {currentStep > step.id ? <Check size={18} /> : <step.icon size={18} />}
            </div>
            <span className="checkout-step-name">{step.name}</span>
            {index < steps.length - 1 && <div className="checkout-step-line" />}
          </div>
        ))}
      </div>

      <div className="checkout-container">
        <div className="checkout-main">
          {/* Step 1: Shipping */}
          {currentStep === 1 && (
            <div className="checkout-section">
              <h2 className="checkout-section-title">
                <Truck size={20} />
                Shipping Information
              </h2>

              {!isAuthenticated && (
                <div className="checkout-guest-notice">
                  <p>Have an account? <Link to="/signin">Sign in</Link> for faster checkout</p>
                </div>
              )}

              <form className="checkout-form">
                <div className="checkout-form-row">
                  <div className="checkout-form-group">
                    <label>First Name *</label>
                    <input type="text" name="firstName" value={shippingInfo.firstName} onChange={handleShippingChange} placeholder="John" required />
                  </div>
                  <div className="checkout-form-group">
                    <label>Last Name *</label>
                    <input type="text" name="lastName" value={shippingInfo.lastName} onChange={handleShippingChange} placeholder="Doe" required />
                  </div>
                </div>

                <div className="checkout-form-row">
                  <div className="checkout-form-group">
                    <label>Email *</label>
                    <input type="email" name="email" value={shippingInfo.email} onChange={handleShippingChange} placeholder="john@example.com" required />
                  </div>
                  <div className="checkout-form-group">
                    <label>Phone *</label>
                    <input type="tel" name="phone" value={shippingInfo.phone} onChange={handleShippingChange} placeholder="(555) 123-4567" required />
                  </div>
                </div>

                <div className="checkout-form-group full">
                  <label>Address *</label>
                  <input type="text" name="address" value={shippingInfo.address} onChange={handleShippingChange} placeholder="123 Main Street" required />
                </div>

                <div className="checkout-form-row">
                  <div className="checkout-form-group">
                    <label>City *</label>
                    <input type="text" name="city" value={shippingInfo.city} onChange={handleShippingChange} placeholder="New York" required />
                  </div>
                  <div className="checkout-form-group">
                    <label>State *</label>
                    <input type="text" name="state" value={shippingInfo.state} onChange={handleShippingChange} placeholder="NY" required />
                  </div>
                  <div className="checkout-form-group">
                    <label>ZIP Code *</label>
                    <input type="text" name="zipCode" value={shippingInfo.zipCode} onChange={handleShippingChange} placeholder="10001" required />
                  </div>
                </div>

                <div className="checkout-form-group full">
                  <label>Country</label>
                  <select name="country" value={shippingInfo.country} onChange={handleShippingChange}>
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="Mexico">Mexico</option>
                  </select>
                </div>
              </form>
            </div>
          )}

          {/* Step 2: Payment */}
          {currentStep === 2 && (
            <div className="checkout-section">
              <h2 className="checkout-section-title">
                <CreditCard size={20} />
                Payment Information
              </h2>

              <div className="checkout-secure-notice">
                <Lock size={16} />
                <span>Your payment information is secure and encrypted</span>
              </div>

              <form className="checkout-form">
                <div className="checkout-form-group full">
                  <label>Card Number *</label>
                  <input
                    type="text"
                    name="cardNumber"
                    value={paymentInfo.cardNumber}
                    onChange={handlePaymentChange}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                    required
                  />
                </div>

                <div className="checkout-form-group full">
                  <label>Name on Card *</label>
                  <input type="text" name="cardName" value={paymentInfo.cardName} onChange={handlePaymentChange} placeholder="John Doe" required />
                </div>

                <div className="checkout-form-row">
                  <div className="checkout-form-group">
                    <label>Expiry Date *</label>
                    <input type="text" name="expiry" value={paymentInfo.expiry} onChange={handlePaymentChange} placeholder="MM/YY" maxLength="5" required />
                  </div>
                  <div className="checkout-form-group">
                    <label>CVV *</label>
                    <input type="text" name="cvv" value={paymentInfo.cvv} onChange={handlePaymentChange} placeholder="123" maxLength="4" required />
                  </div>
                </div>

                <label className="checkout-checkbox">
                  <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} />
                  <span>Billing address same as shipping</span>
                </label>
              </form>

              <div className="checkout-accepted-cards">
                <span>We accept:</span>
                <div className="checkout-card-icons">
                  <span className="card-icon visa">VISA</span>
                  <span className="card-icon mastercard">MC</span>
                  <span className="card-icon amex">AMEX</span>
                  <span className="card-icon discover">DISC</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && (
            <div className="checkout-section">
              <h2 className="checkout-section-title">
                <Check size={20} />
                Review Your Order
              </h2>

              <div className="checkout-review">
                <div className="checkout-review-section">
                  <h3>Shipping Address</h3>
                  <p>
                    {shippingInfo.firstName} {shippingInfo.lastName}<br />
                    {shippingInfo.address}<br />
                    {shippingInfo.city}, {shippingInfo.state} {shippingInfo.zipCode}<br />
                    {shippingInfo.country}
                  </p>
                  <button className="checkout-edit-btn" onClick={() => setCurrentStep(1)}>Edit</button>
                </div>

                <div className="checkout-review-section">
                  <h3>Payment Method</h3>
                  <p>
                    Card ending in {paymentInfo.cardNumber.slice(-4)}<br />
                    {paymentInfo.cardName}
                  </p>
                  <button className="checkout-edit-btn" onClick={() => setCurrentStep(2)}>Edit</button>
                </div>

                <div className="checkout-review-items">
                  <h3>Order Items ({getTotalItems()})</h3>
                  {cartItems.map((item) => (
                    <div key={`${item.product.id}-${item.selectedSize}-${item.selectedColor.name}`} className="checkout-review-item">
                      <img src={item.product.image} alt={item.product.name} />
                      <div className="checkout-review-item-info">
                        <p className="checkout-review-item-brand">{item.product.brand}</p>
                        <p className="checkout-review-item-name">{item.product.name}</p>
                        <p className="checkout-review-item-details">
                          Size: {item.selectedSize} | Color: {item.selectedColor.name} | Qty: {item.quantity}
                        </p>
                      </div>
                      <p className="checkout-review-item-price">
                        ${((item.product.salePrice || item.product.price) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="checkout-navigation">
            {currentStep > 1 && (
              <button className="checkout-back-btn" onClick={() => setCurrentStep((prev) => prev - 1)}>
                <ArrowLeft size={16} /> Back
              </button>
            )}
            {currentStep < 3 ? (
              <button className="checkout-next-btn" onClick={handleNextStep}>
                Continue
              </button>
            ) : (
              <button className="checkout-place-btn" onClick={handlePlaceOrder} disabled={isProcessing}>
                {isProcessing ? 'Processing...' : 'Place Order'}
              </button>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <div className="checkout-summary">
          <h2 className="checkout-summary-title">
            <ShoppingBag size={20} />
            Order Summary
          </h2>

          <div className="checkout-summary-items">
            {cartItems.map((item) => (
              <div key={`${item.product.id}-${item.selectedSize}-${item.selectedColor.name}`} className="checkout-summary-item">
                <div className="checkout-summary-item-image">
                  <img src={item.product.image} alt={item.product.name} />
                  <span className="checkout-summary-item-qty">{item.quantity}</span>
                </div>
                <div className="checkout-summary-item-info">
                  <p className="checkout-summary-item-name">{item.product.name}</p>
                  <p className="checkout-summary-item-details">
                    {item.selectedSize} / {item.selectedColor.name}
                  </p>
                </div>
                <p className="checkout-summary-item-price">
                  ${((item.product.salePrice || item.product.price) * item.quantity).toFixed(2)}
                </p>
              </div>
            ))}
          </div>

          <div className="checkout-summary-totals">
            <div className="checkout-summary-row">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="checkout-summary-row">
              <span>Shipping</span>
              <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
            </div>
            <div className="checkout-summary-row">
              <span>Tax</span>
              <span>${tax.toFixed(2)}</span>
            </div>
            <div className="checkout-summary-row total">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          {shipping === 0 && (
            <div className="checkout-free-shipping">
              <Truck size={16} />
              <span>You qualify for FREE shipping!</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
